import { Controller } from "@hotwired/stimulus"

// Drives the broadcast composer: the channel segmented control toggles the
// subject field and the email/SMS preview blocks; merge-field chips insert a
// {{token}} at the cursor; typing live-renders a preview against a sample
// recipient (its field values are passed in as JSON) with an SMS char/segment
// counter. Progressive enhancement — the form posts fine without any of this.
export default class extends Controller {
  static targets = [
    "channel", "subjectField", "subjectInput", "body",
    "emailPreview", "smsPreview", "previewSubject", "previewBody", "smsBody",
    "charCount"
  ]
  static values = { sample: Object }

  connect() {
    this.lastField = this.hasBodyTarget ? this.bodyTarget : null
    this.trackFocus()
    this.channelChanged()
    this.render()
  }

  trackFocus() {
    ;[this.bodyTarget, this.subjectInputTarget].forEach((el) => {
      if (el) el.addEventListener("focus", () => { this.lastField = el })
    })
  }

  channel() {
    const checked = this.channelTargets.find((r) => r.checked)
    return checked ? checked.value : "email"
  }

  channelChanged() {
    const channel = this.channel()
    const includesEmail = channel === "email" || channel === "both"
    const includesSms = channel === "sms" || channel === "both"

    this.toggle(this.subjectFieldTarget, includesEmail)
    if (this.hasEmailPreviewTarget) this.toggle(this.emailPreviewTarget, includesEmail)
    if (this.hasSmsPreviewTarget) this.toggle(this.smsPreviewTarget, includesSms)
    this.toggle(this.charCountTarget, includesSms)
    this.render()
  }

  // Insert a {{token}} at the cursor of the most recently focused field.
  insertField(event) {
    const token = `{{${event.currentTarget.dataset.field}}}`
    const el = this.lastField || (this.hasBodyTarget ? this.bodyTarget : null)
    if (!el) return

    const start = el.selectionStart ?? el.value.length
    const end = el.selectionEnd ?? el.value.length
    el.value = el.value.slice(0, start) + token + el.value.slice(end)
    const caret = start + token.length
    el.setSelectionRange(caret, caret)
    el.focus()
    this.render()
  }

  render() {
    const subject = this.hasSubjectInputTarget ? this.subjectInputTarget.value : ""
    const body = this.hasBodyTarget ? this.bodyTarget.value : ""

    if (this.hasPreviewSubjectTarget) {
      this.previewSubjectTarget.textContent = this.merge(subject) || "(no subject)"
    }
    if (this.hasPreviewBodyTarget) {
      this.previewBodyTarget.textContent = this.merge(body) || "Your message will appear here."
    }
    if (this.hasSmsBodyTarget) {
      this.smsBodyTarget.textContent = this.merge(body) || "Your message will appear here."
    }
    this.updateCharCount(this.merge(body))
  }

  // Substitute known {{tokens}} with the sample recipient's values; unknown
  // tokens are left literal (mirrors the server-side MergeFields contract).
  merge(text) {
    const sample = this.sampleValue || {}
    return (text || "").replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (match, key) => {
      const value = sample[key.toLowerCase()]
      return value === undefined ? match : value
    })
  }

  updateCharCount(text) {
    if (!this.hasCharCountTarget) return
    const len = (text || "").length
    const segments = len === 0 ? 0 : Math.ceil(len / 153) || 1
    const single = len <= 160
    const segs = single ? 1 : segments
    this.charCountTarget.textContent = `${len} chars · ${segs} SMS ${segs === 1 ? "segment" : "segments"}`
    this.charCountTarget.classList.toggle("text-warning-600", len > 160)
    this.charCountTarget.classList.toggle("text-secondary-500", len <= 160)
  }

  toggle(el, show) {
    if (el) el.classList.toggle("hidden", !show)
  }
}
